import React, { useState, useMemo, useEffect, useRef } from 'react';
import { formatDistanceToNow } from 'date-fns';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { 
  User,
  Activity,
  RefreshCw,
  Sparkles,
  AlertCircle,
  AlertTriangle,
  CheckCircle,
  TrendingUp,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { useTalentData } from '@/hooks/useTalentData';
import { useAthleteSignalIntel } from '@/hooks/useAthleteSignalIntel';
import { useSignalIntelligence, type SignalEntity } from '@/hooks/useSignalIntelligence';
import { cn } from '@/lib/utils';
import { SignalTabContent } from '@/components/signal/SignalTabContent';

const getEntityEmoji = (type: SignalEntity['type']) => {
  switch (type) {
    case 'self': return '🏃';
    case 'brand': return '🏢';
    case 'venture': return '💼';
    case 'talent': return '🏃';
    case 'agent': return '🤝';
    default: return '📊';
  }
};

const computeSentiment = (results: any[]) => {
  const positive = results.filter(r => r.sentiment === 'positive').length;
  const negative = results.filter(r => r.sentiment === 'negative').length;
  const neutral = results.length - positive - negative;
  const overallScore = results.length > 0 ? (positive / results.length) * 100 : 0;
  const overallSentiment = positive >= negative && positive >= neutral ? 'positive' : negative > positive ? 'negative' : 'neutral';
  return { positive, neutral, negative, overallScore, overallSentiment: overallSentiment as 'positive' | 'neutral' | 'negative' };
};

export const AthleteSignalSection = () => {
  const { claimedAthlete, claimedCreator, athleteData, loading: athleteLoading } = useTalentData();
  const { signalData, isLoading, fetchSignalIntel } = useAthleteSignalIntel();
  const [dateRange, setDateRange] = useState<'7d' | '30d' | '90d'>('7d');
  const { data: networkData, isLoading: networkLoading, refresh: refreshNetwork } = useSignalIntelligence(dateRange);
  const [activeTab, setActiveTab] = useState('all');
  const [partnerFilter, setPartnerFilter] = useState('all-partners');
  const hasFetchedRef = useRef(false);

  // Auto-fetch on mount when no cached data exists
  useEffect(() => {
    if (hasFetchedRef.current) return;
    if (athleteLoading || isLoading || networkLoading) return;
    if (!claimedAthlete?.id || !claimedAthlete?.name) return;
    if (signalData || networkData) return;

    hasFetchedRef.current = true;
    fetchSignalIntel(
      claimedAthlete.id,
      claimedAthlete.name,
      claimedAthlete.current_team_name || undefined,
      claimedAthlete.sport
    );
    refreshNetwork();
  }, [athleteLoading, isLoading, networkLoading, claimedAthlete, signalData, networkData]);


  const handleRefresh = () => {
    if (claimedAthlete?.id && claimedAthlete?.name) {
      fetchSignalIntel(
        claimedAthlete.id,
        claimedAthlete.name,
        claimedAthlete.current_team_name || undefined,
        claimedAthlete.sport,
        true
      );
    }
  };

  // Build entity list: self + network partners
  const networkEntities = (networkData?.entities || []).filter(e => e.type !== 'self');
  const allEntities: { id: string; name: string; type: SignalEntity['type'] }[] = useMemo(() => {
    const list: { id: string; name: string; type: SignalEntity['type'] }[] = [];
    if (claimedAthlete?.name) {
      list.push({ id: 'self', name: claimedAthlete.name, type: 'self' });
    }
    for (const e of networkEntities) {
      list.push({ id: e.id, name: e.name, type: e.type });
    }
    return list;
  }, [claimedAthlete?.name, networkEntities]);

  // Partner entities (non-self)
  const partnerEntities = useMemo(() => allEntities.filter(e => e.type !== 'self'), [allEntities]);

  // Identify self entity's Google results from networkData
  const selfEntityId = networkData?.entities?.find(e => e.type === 'self')?.id;
  const selfGoogleResults = selfEntityId ? (networkData?.resultsByEntity?.[selfEntityId] || []) : [];

  // Network results excluding self (for partnerships tab)
  const partnerOnlyResults = useMemo(() => {
    if (!networkData?.combinedResults?.length) return [];
    if (partnerEntities.length === 0) return [];
    
    if (selfGoogleResults.length) {
      const selfSet = new Set(selfGoogleResults);
      return networkData.combinedResults.filter(r => !selfSet.has(r));
    }
    
    const partnerIds = new Set(partnerEntities.map(e => e.id));
    const partnerResults: any[] = [];
    for (const id of partnerIds) {
      const entityResults = networkData.resultsByEntity?.[id] || [];
      partnerResults.push(...entityResults);
    }
    return partnerResults;
  }, [networkData?.combinedResults, selfGoogleResults, partnerEntities, networkData?.resultsByEntity]);

  // Filtered data based on active tab + partner filter
  const filtered = useMemo(() => {
    if (activeTab === 'all') {
      const espnResults = signalData?.analyzedResults || [];
      const netResults = networkData?.combinedResults || [];
      const combined = [...espnResults, ...netResults];
      const selfAlerts = signalData?.alerts || [];
      const netAlerts = networkData?.alerts || [];
      return {
        results: combined,
        alerts: [...selfAlerts, ...netAlerts],
        sentiment: computeSentiment(combined),
      };
    }
    if (activeTab === 'personal') {
      // Merge ESPN results + Google self results
      const espnResults = signalData?.analyzedResults || [];
      const combined = [...espnResults, ...selfGoogleResults];
      return {
        results: combined,
        alerts: signalData?.alerts || [],
        sentiment: computeSentiment(combined),
      };
    }
    // partnerships tab — exclude self results
    if (partnerFilter === 'all-partners') {
      const netAlerts = networkData?.alerts || [];
      return { results: partnerOnlyResults, alerts: netAlerts, sentiment: computeSentiment(partnerOnlyResults) };
    }
    const results = networkData?.resultsByEntity?.[partnerFilter] || [];
    const entityName = partnerEntities.find(e => e.id === partnerFilter)?.name?.toLowerCase() || '';
    const alerts = (networkData?.alerts || []).filter(a => a.message?.toLowerCase().includes(entityName));
    return { results, alerts, sentiment: computeSentiment(results) };
  }, [activeTab, partnerFilter, signalData, networkData, partnerEntities, selfGoogleResults, partnerOnlyResults]);

  // No claimed athlete
  const isAthleteType = athleteData?.talent?.talent_type === 'athlete';
  
  if (!athleteLoading && !claimedAthlete && !claimedCreator) {
    return (
      <div className="space-y-6">
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12 text-center">
            <User className="h-12 w-12 text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold mb-2">
              {isAthleteType ? 'Claim Your Athlete Profile' : 'Claim Your Creator Profile'}
            </h3>
            <p className="text-sm text-muted-foreground max-w-md mb-2">
              Claim your profile to unlock personalized Signal Intelligence — real-time monitoring of your media mentions, brand sentiment, and market trends.
            </p>
            <Button className="mt-4" variant="default" asChild>
              <Link to={isAthleteType ? '/claim-athlete' : '/claim-creator'}>Claim Your Profile</Link>
            </Button>
          </CardContent>
        </Card>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 opacity-50 pointer-events-none">
          <Card className="p-4 text-center">
            <Sparkles className="h-8 w-8 text-primary mx-auto mb-2" />
            <h4 className="font-medium text-sm">Media Mentions</h4>
            <p className="text-xs text-muted-foreground">Track news, articles, and social mentions in real time</p>
          </Card>
          <Card className="p-4 text-center">
            <TrendingUp className="h-8 w-8 text-primary mx-auto mb-2" />
            <h4 className="font-medium text-sm">Sentiment Analysis</h4>
            <p className="text-xs text-muted-foreground">AI-powered sentiment scoring across all coverage</p>
          </Card>
          <Card className="p-4 text-center">
            <AlertTriangle className="h-8 w-8 text-primary mx-auto mb-2" />
            <h4 className="font-medium text-sm">Smart Alerts</h4>
            <p className="text-xs text-muted-foreground">Get notified about important mentions and trends</p>
          </Card>
        </div>
      </div>
    );
  }

  // Loading
  if (athleteLoading || isLoading) {
    return (
      <Card>
        <CardContent className="flex flex-col items-center justify-center py-16 text-center">
          <div className="relative">
            <Sparkles className="h-12 w-12 text-primary animate-pulse" />
            <div className="absolute inset-0 animate-ping opacity-20">
              <Sparkles className="h-12 w-12 text-primary" />
            </div>
          </div>
          <p className="mt-4 text-lg font-medium">Analyzing your signal intelligence...</p>
          <p className="text-sm text-muted-foreground mt-1">
            Fetching news and analyzing sentiment for {claimedAthlete?.name || 'your profile'}
          </p>
        </CardContent>
      </Card>
    );
  }

  // Error
  if (signalData?.error) {
    return (
      <Card>
        <CardContent className="flex flex-col items-center justify-center py-12 text-center">
          <AlertCircle className="h-10 w-10 text-destructive mb-3" />
          <p className="text-destructive font-medium">Failed to load signal intelligence</p>
          <p className="text-sm text-muted-foreground mt-1">{signalData.error}</p>
          <Button variant="outline" size="sm" onClick={handleRefresh} className="mt-4">
            <RefreshCw className="h-4 w-4 mr-2" />
            Try Again
          </Button>
        </CardContent>
      </Card>
    );
  }

  const hasAnyData = (signalData?.analyzedResults?.length || 0) > 0 || 
                     (signalData?.alerts?.length || 0) > 0 ||
                     (networkData?.combinedResults?.length || 0) > 0;

  return (
    <Card>
      <CardHeader className="px-3 sm:px-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
          <div className="flex items-center gap-2">
            <Activity className="h-5 w-5 text-primary" />
            <CardTitle className="text-base sm:text-lg">Signal Intelligence</CardTitle>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <div className="flex items-center rounded-lg border bg-muted/50 p-0.5">
              {(['7d', '30d', '90d'] as const).map((range) => (
                <button
                  key={range}
                  onClick={() => setDateRange(range)}
                  className={cn(
                    'px-2 sm:px-3 py-1 text-xs font-medium rounded-md transition-colors',
                    dateRange === range
                      ? 'bg-background text-foreground shadow-sm'
                      : 'text-muted-foreground hover:text-foreground'
                  )}
                >
                  {range === '7d' ? '7D' : range === '30d' ? '30D' : '90D'}
                </button>
              ))}
            </div>
            <Badge variant="outline" className="gap-1 text-xs max-w-[120px] sm:max-w-none truncate">
              <CheckCircle className="h-3 w-3 flex-shrink-0" />
              <span className="truncate">{claimedAthlete?.name}</span>
            </Badge>
            <Button variant="ghost" size="icon" onClick={handleRefresh} className="h-8 w-8 flex-shrink-0">
              <RefreshCw className={cn('h-4 w-4', isLoading && 'animate-spin')} />
            </Button>
          </div>
        </div>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1">
          <p className="text-xs sm:text-sm text-muted-foreground">
            Real-time coverage and sentiment analysis
          </p>
          {(signalData?.lastUpdated || networkData?.lastUpdated) && (
            <p className="text-xs text-muted-foreground">
              Last updated: {formatDistanceToNow(signalData?.lastUpdated || networkData?.lastUpdated!, { addSuffix: true })}
            </p>
          )}
        </div>
      </CardHeader>
      <CardContent className="px-3 sm:px-6">
        {!hasAnyData ? (
          <div className="flex flex-col items-center justify-center py-8 text-center">
            <Sparkles className="h-10 w-10 text-muted-foreground mb-3" />
            <p className="text-muted-foreground">No recent coverage found</p>
            <Button variant="outline" size="sm" onClick={handleRefresh} className="mt-4">
              <RefreshCw className="h-4 w-4 mr-2" />
              Search Again
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            <Tabs value={activeTab} onValueChange={(v) => { setActiveTab(v); if (v !== 'partnerships') setPartnerFilter('all-partners'); }}>
              <div className="flex items-center gap-3 flex-wrap">
                <TabsList className="w-fit">
                  <TabsTrigger value="all">📊 All</TabsTrigger>
                  <TabsTrigger value="personal">🏃 Personal</TabsTrigger>
                  <TabsTrigger value="partnerships">🤝 Partnerships</TabsTrigger>
                </TabsList>
                {activeTab === 'partnerships' && partnerEntities.length > 0 && (
                  <Select value={partnerFilter} onValueChange={setPartnerFilter}>
                    <SelectTrigger className="w-[200px] h-9">
                      <SelectValue placeholder="Filter partners" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all-partners">All Partners</SelectItem>
                      {partnerEntities.map(e => (
                        <SelectItem key={e.id} value={e.id}>
                          {getEntityEmoji(e.type)} {e.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>
            </Tabs>

            <SignalTabContent
              analyzedResults={filtered.results}
              sentimentBreakdown={filtered.sentiment}
              alerts={filtered.alerts}
            />
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default AthleteSignalSection;
